// src/utils/chicagoTime.js
// The ONE clock for the browser app: Minnesota time (America/Chicago, Central
// with daylight saving), whatever time zone the phone or laptop is set to.
//
// Henry's rule: "today", "this year", "9:05 AM" and every other calendar answer
// is the Minnesota answer. A device clock in Arizona or on a trip to Europe must
// never move a due date, a late pill or a year total.
//
// Built on Intl.DateTimeFormat with timeZone "America/Chicago" (built into every
// browser and Node), so no extra package. Nothing here imports another app file,
// so any helper can use it without a circular import.

export const CHICAGO_TZ = "America/Chicago";

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})/;

let partsFmt = null;
function getPartsFmt() {
  if (!partsFmt) {
    partsFmt = new Intl.DateTimeFormat("en-US", {
      timeZone: CHICAGO_TZ,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      weekday: "short",
    });
  }
  return partsFmt;
}

const WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

function toMs(v) {
  if (v == null || v === "") return NaN;
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v;
  if (typeof v === "object" && typeof v.toMillis === "function") return v.toMillis(); // Firestore Timestamp
  if (typeof v === "object" && typeof v.seconds === "number") return v.seconds * 1000;
  if (typeof v === "string" && /^\d+$/.test(v.trim())) return Number(v.trim());
  return new Date(v).getTime();
}

const pad2 = (n) => String(n).padStart(2, "0");

// The Minnesota calendar and clock at an instant:
// { year, month (1-12), day, hour (0-23), minute, second, weekday (0 = Sunday) }.
// null for a blank or unreadable value (pass Date.now() for "right now").
export function chicagoParts(msOrDate) {
  const ms = toMs(msOrDate);
  if (!Number.isFinite(ms)) return null;
  const out = {};
  for (const p of getPartsFmt().formatToParts(new Date(ms))) {
    if (p.type === "weekday") out.weekday = WEEKDAYS[p.value];
    else if (p.type !== "literal") out[p.type] = Number(p.value);
  }
  if (out.hour === 24) out.hour = 0; // very old engines print midnight as 24
  return out;
}

// "YYYY-MM-DD" of the Minnesota day that instant falls on. "" when unreadable.
export function chicagoYMD(msOrDate) {
  const p = chicagoParts(msOrDate);
  return p ? `${p.year}-${pad2(p.month)}-${pad2(p.day)}` : "";
}

// Today's date in Minnesota, "YYYY-MM-DD".
export function chicagoTodayYMD(now = Date.now()) {
  return chicagoYMD(now);
}

export function chicagoYear(now = Date.now()) {
  const p = chicagoParts(now);
  return p ? p.year : NaN;
}

// 1-12
export function chicagoMonth(now = Date.now()) {
  const p = chicagoParts(now);
  return p ? p.month : NaN;
}

export function chicagoHour(now = Date.now()) {
  const p = chicagoParts(now);
  return p ? p.hour : NaN;
}

// The Minnesota time of day as "HH:MM" (24-hour), the shape open house start times use.
export function chicagoHHMM(now = Date.now()) {
  const p = chicagoParts(now);
  return p ? `${pad2(p.hour)}:${pad2(p.minute)}` : "";
}

// MM/DD/YYYY in Minnesota. "" when unreadable.
export function fmtChicagoMdy(msOrDate) {
  const p = chicagoParts(msOrDate);
  return p ? `${pad2(p.month)}/${pad2(p.day)}/${p.year}` : "";
}

// "9:05 AM" in Minnesota. "" when unreadable.
export function fmtChicagoTime(msOrDate) {
  const p = chicagoParts(msOrDate);
  if (!p) return "";
  const h12 = ((p.hour + 11) % 12) + 1;
  return `${h12}:${pad2(p.minute)} ${p.hour >= 12 ? "PM" : "AM"}`;
}

// "09/27/2026 9:05 AM" in Minnesota. "" when unreadable.
export function fmtChicagoDateTime(msOrDate) {
  const d = fmtChicagoMdy(msOrDate);
  return d ? `${d} ${fmtChicagoTime(msOrDate)}` : "";
}

// The year of a "YYYY-MM-DD" string, read from the text (never new Date(ymd),
// which is UTC midnight = the evening before in Minnesota). null when not a date.
export function ymdYear(ymd) {
  const m = YMD_RE.exec(String(ymd == null ? "" : ymd).trim());
  return m ? Number(m[1]) : null;
}

// Calendar arithmetic on the string via Date.UTC, so a daylight-saving switch can never move a day.
export function addDaysYMD(ymd, n) {
  const m = YMD_RE.exec(String(ymd || ""));
  if (!m) return "";
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + Number(n || 0)))
    .toISOString()
    .slice(0, 10);
}

// "YYYY-MM-DD" plus whole months; a day past the end of the new month lands on
// its last day (01/31 + 1 month = 02/28). "" when not a date.
export function addMonthsYMD(ymd, n) {
  const m = YMD_RE.exec(String(ymd || ""));
  if (!m) return "";
  const total = Number(m[1]) * 12 + (Number(m[2]) - 1) + Number(n || 0);
  const y = Math.floor(total / 12);
  const mo = total - y * 12; // 0-11
  const last = new Date(Date.UTC(y, mo + 1, 0)).getUTCDate();
  const d = Math.min(Number(m[3]), last);
  return `${String(y).padStart(4, "0")}-${pad2(mo + 1)}-${pad2(d)}`;
}

// True for a plain "YYYY-MM-DD" string.
export function isYMD(v) {
  return typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);
}

// Whole days from ymdA to ymdB (b - a). NaN when either is not a date.
export function diffDaysYMD(ymdA, ymdB) {
  const a = YMD_RE.exec(String(ymdA || ""));
  const b = YMD_RE.exec(String(ymdB || ""));
  if (!a || !b) return NaN;
  const ua = Date.UTC(Number(a[1]), Number(a[2]) - 1, Number(a[3]));
  const ub = Date.UTC(Number(b[1]), Number(b[2]) - 1, Number(b[3]));
  return Math.round((ub - ua) / 86400000);
}

// 0 = Sunday for a "YYYY-MM-DD" calendar date (zone-free).
export function ymdWeekday(ymd) {
  const m = YMD_RE.exec(String(ymd || ""));
  if (!m) return NaN;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
}

// Minutes Minnesota is ahead of UTC at that instant (-300 in summer, -360 in winter).
function offsetMinutes(ms) {
  const p = chicagoParts(ms);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - Math.floor(ms / 1000) * 1000) / 60000);
}

// The instant (ms) of a Minnesota wall-clock time on a "YYYY-MM-DD" day,
// daylight-saving correct. NaN when unreadable.
export function chicagoWallMs(ymd, hour = 0, minute = 0) {
  const m = YMD_RE.exec(String(ymd || ""));
  if (!m || !Number.isFinite(Number(hour)) || !Number.isFinite(Number(minute))) return NaN;
  const guess = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(hour), Number(minute));
  const off1 = offsetMinutes(guess);
  let ms = guess - off1 * 60000;
  const off2 = offsetMinutes(ms);
  if (off2 !== off1) ms = guess - off2 * 60000;
  return ms;
}

// The instant (ms) of 00:00 Minnesota time on that day, daylight-saving correct.
export function chicagoMidnightMs(ymd) {
  return chicagoWallMs(ymd, 0, 0);
}

// ---------------------------------------------------------------------------
// Display helpers that keep a screen's existing date STYLE but pin the zone.

// Like date.toLocaleString(locale, options) but always read on the Minnesota
// clock. kind: "datetime" (default), "date" or "time" mirrors
// toLocaleString / toLocaleDateString / toLocaleTimeString. "" when unreadable.
export function fmtChicagoLocale(msOrDate, options = {}, kind = "datetime", locale = "en-US") {
  const ms = toMs(msOrDate);
  if (!Number.isFinite(ms)) return "";
  const d = new Date(ms);
  const opts = { ...(options || {}), timeZone: CHICAGO_TZ };
  if (kind === "date") return d.toLocaleDateString(locale, opts);
  if (kind === "time") return d.toLocaleTimeString(locale, opts);
  return d.toLocaleString(locale, opts);
}

// A plain "YYYY-MM-DD" calendar date in any toLocaleDateString style, read from
// the text so no time zone can move it a day. "" when not a date.
export function fmtYMDLocale(ymd, options = {}, locale = "en-US") {
  const m = YMD_RE.exec(String(ymd == null ? "" : ymd).trim());
  if (!m) return "";
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
  return d.toLocaleDateString(locale, { ...(options || {}), timeZone: "UTC" });
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// moment-style format on the Minnesota clock (moment(x).format(pattern)
// replacement). Tokens: YYYY YY MMMM MMM MM M DD D Do dddd ddd HH H hh h mm ss A a.
// Text inside [brackets] is copied as-is. "" when unreadable.
export function fmtChicagoPattern(msOrDate, pattern = "MM/DD/YYYY") {
  const p = chicagoParts(msOrDate);
  if (!p) return "";
  const h12 = ((p.hour + 11) % 12) + 1;
  const ord = (n) => {
    const s = n % 100;
    if (s >= 11 && s <= 13) return `${n}th`;
    return `${n}${{ 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th"}`;
  };
  const map = {
    YYYY: String(p.year),
    YY: String(p.year).slice(-2),
    MMMM: MONTHS[p.month - 1],
    MMM: MONTHS[p.month - 1].slice(0, 3),
    MM: pad2(p.month),
    M: String(p.month),
    DD: pad2(p.day),
    Do: ord(p.day),
    D: String(p.day),
    dddd: DAY_NAMES[p.weekday],
    ddd: DAY_NAMES[p.weekday].slice(0, 3),
    HH: pad2(p.hour),
    H: String(p.hour),
    hh: pad2(h12),
    h: String(h12),
    mm: pad2(p.minute),
    ss: pad2(p.second),
    A: p.hour >= 12 ? "PM" : "AM",
    a: p.hour >= 12 ? "pm" : "am",
  };
  return String(pattern).replace(
    /\[([^\]]*)\]|YYYY|YY|MMMM|MMM|MM|M|DD|Do|D|dddd|ddd|HH|H|hh|h|mm|ss|A|a/g,
    (tok, lit) => (lit !== undefined ? lit : map[tok])
  );
}

// First day of the Minnesota month containing that instant, "YYYY-MM-01".
export function chicagoMonthStartYMD(now = Date.now()) {
  const p = chicagoParts(now);
  return p ? `${p.year}-${pad2(p.month)}-01` : "";
}
